import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Users, Target, TrendingUp, BarChart3, Award, Loader2, ChevronRight, Copy, Check, Sparkles, BookOpen, ArrowLeft } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiClient } from '../api/client';
import { useParams, useNavigate } from 'react-router-dom';

const CohortJourneyDashboard = () => {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [communityBadges, setCommunityBadges] = useState([]);
  const [savedIdeas, setSavedIdeas] = useState(() => JSON.parse(localStorage.getItem('hibff_saved_ideas') || '[]'));
  const [copiedText, setCopiedText] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.request(`/api/org/journeys/${journeyId}/metrics`),
      apiClient.request(`/api/org/journeys/${journeyId}/community-badges`),
    ]).then(([m, b]) => { setMetrics(m); setCommunityBadges(b.badges || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [journeyId]);

  const saveIdea = (text) => {
    const idea = { text, journey: metrics?.journey?.name, date: new Date().toISOString().slice(0, 10), notes: '' };
    const updated = [idea, ...savedIdeas];
    setSavedIdeas(updated);
    localStorage.setItem('hibff_saved_ideas', JSON.stringify(updated));
  };

  const copyCelebration = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={24} /></div></DashboardLayout>;
  if (!metrics) return <DashboardLayout><p className="p-8 text-gray-500">Journey not found</p></DashboardLayout>;

  const j = metrics.journey;
  const c = metrics.cohort;
  const e = metrics.engagement;
  const skillData = (metrics.skill_focus || []).map(s => ({ name: s.area, count: s.count }));

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="cohort-journey-dashboard">
        <button onClick={() => navigate('/journeys')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={16} /> All Journeys
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">{j.name}</h1>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${j.context === 'school' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{j.context}</span>
          </div>
          <p className="text-sm text-gray-500">Pace: {j.pace} | Modules: AvA Practice, Real-World Challenges, Pathways</p>
        </div>

        {metrics.anonymised ? (
          <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl text-center">
            <p className="text-amber-800 font-medium">We hide group data when fewer than 3 learners are active to protect privacy.</p>
          </div>
        ) : (
          <>
            {/* Tile A + B + C + D */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* A — Activity Snapshot */}
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Users size={16} className="text-blue-500" /> Activity</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Active learners</span><span className="font-bold">{c.active_this_week} / {c.total_learners}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Avg sessions/learner</span><span className="font-bold">{c.avg_sessions_per_learner}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Real-world actions</span><span className="font-bold">{e.challenges_this_month}</span></div>
                </div>
              </div>

              {/* B — Skill Focus */}
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-purple-500" /> Skill Focus</h3>
                {skillData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={skillData}><XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis hide /><Tooltip /><Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} /></BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-gray-400">No data yet</p>}
              </div>

              {/* C — Pathway Engagement */}
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Target size={16} className="text-amber-500" /> Pathways</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Active</span><span className="font-bold">{e.active_pathways}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Completed</span><span className="font-bold">{e.completed_pathways}</span></div>
                </div>
              </div>

              {/* D — Suggestions */}
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Sparkles size={16} className="text-green-500" /> Suggestions</h3>
                {(metrics.suggestions || []).length === 0 ? <p className="text-sm text-gray-400">No suggestions yet</p> : (
                  <div className="space-y-2">
                    {metrics.suggestions.map((s, i) => (
                      <div key={i} className="text-xs text-gray-700 p-2 bg-green-50 rounded-lg">
                        <p className="mb-1">{s}</p>
                        <button onClick={() => saveIdea(s)} className="text-green-600 font-medium hover:text-green-800 text-[10px]">Save as activity idea</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Community Badges */}
            {communityBadges.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Award size={16} className="text-amber-500" /> Community Badges</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {communityBadges.map(b => {
                    const celebText = `Our group just completed a HiBFF milestone: "${b.title}" — ${b.description}. Building confidence in conversations, teamwork, and empathy together.`;
                    return (
                      <div key={b.badge_id} className="p-3 bg-amber-50 rounded-xl flex items-start gap-3">
                        <Award size={24} className="text-amber-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{b.title}</p>
                          <p className="text-xs text-gray-600">{b.description}</p>
                          <p className="text-[10px] text-gray-400 mt-1">Earned {b.earned_at?.slice(0, 10)}</p>
                          <button onClick={() => copyCelebration(celebText)}
                            className="mt-1 text-[10px] text-amber-600 font-medium flex items-center gap-1 hover:text-amber-800">
                            {copiedText === celebText ? <><Check size={10} /> Copied!</> : <><Copy size={10} /> Copy celebration text</>}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Saved Activity Ideas */}
            {savedIdeas.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><BookOpen size={16} className="text-indigo-500" /> Saved Activity Ideas</h3>
                <div className="space-y-2">
                  {savedIdeas.slice(0, 5).map((idea, i) => (
                    <div key={i} className="p-2 bg-gray-50 rounded-lg text-xs flex items-start justify-between">
                      <div><p className="text-gray-700">{idea.text}</p><p className="text-gray-400 mt-0.5">{idea.journey} · {idea.date}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

// ─── JOURNEY LIST ───────────────────────────────────────────────────────────

const JourneyList = () => {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.request('/api/org/journeys')
      .then(d => setJourneys(d.journeys || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={24} /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Journeys</h1>
        {journeys.length === 0 ? (
          <div className="text-center py-12">
            <Target size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No journeys configured yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {journeys.map(j => (
              <div key={j.journey_id} onClick={() => navigate(`/journeys/${j.journey_id}`)}
                className="p-5 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${j.context === 'school' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{j.context}</span>
                  <span className="text-xs text-gray-400 capitalize">{j.pace} pace</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{j.name}</h3>
                <p className="text-xs text-gray-500">{j.enabled_modules?.length || 0} modules · {j.custom_challenges?.length || 0} custom challenges</p>
                <div className="flex items-center justify-end mt-2"><ChevronRight size={16} className="text-gray-400" /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export { CohortJourneyDashboard, JourneyList };
